import {Observable, of as observableOf} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Store, select} from '@ngrx/store';

import {getAuthenticatedUser, isAuthenticated, State} from '@app/app.reducers';
import {User} from '../model/user';
import {AuthenticatedUserService} from '../service/authenticated-user.service';
import {environment} from '@env/environment';
import {AuthenticationService} from '@app/core/service/authentication.service';

@Injectable()
export class AuthGuard implements CanActivate {

    private loggedIn$: Observable<boolean>;
    private authenticatedUser: User;
    readonly TESTING_QUERY_PARAM = 'testingaftermaintenance';
    readonly TRUE = 'true';

    constructor(private router: Router,
                private authenticatedUserService: AuthenticatedUserService,
                private authenticationService: AuthenticationService,
                private store: Store<State>
    ) {
        this.loggedIn$ = store.pipe(select(isAuthenticated));
        this.store.pipe(select(getAuthenticatedUser))
            .subscribe(user => this.authenticatedUser = user);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

        let allowed = true;
        if (route.data && route.data['permissions']) {
            // Check role org type
            const permittedOrgTypes: string[] = this.authenticatedUserService.getRolesOrgTypes(route.data['permissions']);

            allowed = this.authenticatedUserService.doCheckAuthorization(this.authenticatedUser, route.data['permissions'], permittedOrgTypes);
        }

        return this.loggedIn$
            .pipe(
                map(loggedIn => {
                    const redirectIfOnMaintenance = this.redirectIfOnMaintenance();

                    if (!redirectIfOnMaintenance) {
                        if (loggedIn) {
                            // check permission to url
                            if (!allowed) {
                                this.router.navigate(['']);
                                return false;
                            } else {
                                return true;
                            }
                        } else if (!this.router.url.startsWith('/auth/oauth')) {
                            this.authenticationService.redirectToLoginPage({queryParams: {returnUrl: state.url}});
                        }
                    }
                }),
                catchError((err) => {
                    console.log(err);
                    this.authenticationService.redirectToLoginPage({queryParams: {returnUrl: state.url}});
                    return observableOf(false);
                })
            );
    }

    private redirectIfOnMaintenance(): boolean {
        if (environment.maintenance) {
            const isTestingActive = this.restoreTestingParamToLocalStorage() === this.TRUE;

            if (!isTestingActive) {
                this.router.navigate(['/maintenance']);
                return true;
            }
        }
        return false;
    }

    private restoreTestingParamToLocalStorage(): string {
        return window.localStorage.getItem(this.TESTING_QUERY_PARAM);
    }
}
